import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { apiService } from '../../components/Config/api.service';
import EmployeeSelect from '../../components/Formulaire/EmployeeSelect';
import TableList from '../../components/Tools/TableList';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { enveloppeDe } from '../../components/Utils/enveloppeDe';
import { notify } from '../../components/Utils/Notifications';
import { CLASSE_CHAMP } from './departOutils';
import DrawerCustom from '../../components/Tools/DrawerCustom';
import NouveauDossierDisciplinaire from './NouveauDossierDisciplinaire';
import PanneauDossierDisciplinaire from './PanneauDossierDisciplinaire';
import { ETATS_DOSSIER, EtatDossier, TYPES_ETAPE, messageRefus } from './disciplineOutils';

/**
 * Les dossiers disciplinaires, filtrables par état, type et salarié (contrat
 * C39, Yros/Kavero#15). Avec `employeId`, la liste de la fiche d'un salarié :
 * filtrée sur lui, sans sélecteur ni colonne salarié.
 *
 * L'ordre est celui d'Odoo (date des faits décroissante), sans retri. Un refus
 * s'affiche par `notify` et ne rend AUCUN tableau : un tableau vide dirait
 * « aucun dossier ». Le portail ne reformule pas le corps d'une panne.
 */
function ListeDossiersDisciplinaires({ employeId }) {
  const { t } = useTranslation();
  const [dossiers, setDossiers] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [etat, setEtat] = useState('');
  const [type, setType] = useState('');
  const [salarie, setSalarie] = useState(null);
  const [ouvertId, setOuvertId] = useState(null);
  // Numéro du dernier appel lancé : une réponse d'un appel précédent est ignorée.
  const dernier = useRef(0);
  // `t` hors des dépendances : un rechargement ne doit suivre que les filtres.
  const tRef = useRef(t);
  tRef.current = t;

  const idSalarie = employeId ?? salarie?.value;

  const charger = useCallback(async () => {
    const appel = ++dernier.current;
    // Le proxy refuse un filtre vide : seules les clés renseignées partent.
    const filtres = {};
    if (idSalarie) filtres.employe_id = idSalarie;
    if (etat) filtres.etat = etat;
    if (type) filtres.type = type;
    setChargement(true);
    const reponse = await enveloppeDe(() => apiService.listerDossiersDisciplinaires(filtres));
    if (appel !== dernier.current) return;
    if (reponse.ok) {
      const liste = reponse.dossiers || [];
      setDossiers(liste);
      // Un volet ouvert sur un dossier absent de la liste rechargée se referme pour de bon.
      setOuvertId((id) => (liste.some((d) => d.id === id) ? id : null));
    } else {
      setDossiers(null);
      setOuvertId(null);
      notify(messageRefus(reponse, tRef.current), '', 3);
    }
    setChargement(false);
  }, [idSalarie, etat, type]);

  // Le volet lit la ligne de la liste chargée : aucune route ne lit un dossier seul.
  const ouvert = dossiers?.find((d) => d.id === ouvertId);
  // La ligne est remplacée par le dossier rendu d'Odoo, par SON id (le volet a pu changer).
  const remplacer = (dossier) => setDossiers((liste) => liste?.map((d) => (d.id === dossier.id ? dossier : d)));
  // `etapes/emettre` ne rend que l'étape : remplacement par son `id`, en mise à jour
  // fonctionnelle (la prop du volet peut être périmée), sans rien recalculer.
  const remplacerEtape = (dossierId, etape) => setDossiers((liste) => liste?.map((d) => (d.id === dossierId
    ? { ...d, etapes: (d.etapes || []).map((e) => (e.id === etape.id ? etape : e)) } : d)));
  // Le volet ne s'ouvre que si la liste rechargée contient le dossier créé ; sinon
  // `charger` purge la sélection (jamais de volet fantôme après un changement de filtre).
  const apresCreation = async (dossier) => {
    setOuvertId(dossier.id);
    await charger();
  };

  useEffect(() => {
    charger();
    // Une réponse qui arrive après le démontage n'écrit plus rien.
    return () => { dernier.current += 1; };
  }, [charger]);

  const colonnes = useMemo(
    () => [
      {
        key: 'numero',
        label: t('discipline.colNumero'),
        render: (dossier) => <span data-testid={`dossier-ligne-${dossier.id}`}>{dossier.numero}</span>,
      },
      ...(employeId ? [] : [{ key: 'salarie', label: t('discipline.colSalarie'), render: (dossier) => dossier.employe?.nom || '—' }]),
      { key: 'date_faits', label: t('discipline.colDateFaits'), render: (dossier) => dossier.date_faits || '—' },
      { key: 'etat', label: t('discipline.colEtat'), render: (dossier) => <EtatDossier etat={dossier.etat} /> },
    ],
    [t, employeId]
  );

  return (
    <div className="bg-white p-4 sm:p-6 rounded-2xl shadow-sm border border-slate-100 space-y-4">
      <NouveauDossierDisciplinaire employeId={employeId} onCree={apresCreation} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <label className="block text-sm font-medium">
          {t('discipline.filtreEtat')}
          <select data-testid="dossiers-filtre-etat" value={etat} onChange={(e) => setEtat(e.target.value)} className={CLASSE_CHAMP}>
            <option value="">{t('discipline.tous')}</option>
            {ETATS_DOSSIER.map((code) => (
              <option key={code} value={code}>{t(`discipline.etats.${code}`, { defaultValue: code })}</option>
            ))}
          </select>
        </label>
        <label className="block text-sm font-medium">
          {t('discipline.filtreType')}
          <select data-testid="dossiers-filtre-type" value={type} onChange={(e) => setType(e.target.value)} className={CLASSE_CHAMP}>
            <option value="">{t('discipline.tous')}</option>
            {TYPES_ETAPE.map((code) => (
              <option key={code} value={code}>{t(`discipline.types.${code}`, { defaultValue: code })}</option>
            ))}
          </select>
        </label>
        {!employeId && (
          <div className="text-sm font-medium">
            <span>{t('discipline.filtreSalarie')}</span>
            <div className="mt-1">
              <EmployeeSelect value={salarie} onChange={setSalarie} />
            </div>
          </div>
        )}
      </div>

      {chargement && <LoadingBarWithStyles />}
      {!chargement && dossiers && (
        <TableList columns={colonnes} data={dossiers} showFilter={false} onRowClick={(d) => setOuvertId(d.id)} />
      )}
      {ouvert && (
        <DrawerCustom isOpen setIsOpen={() => setOuvertId(null)}>
          <PanneauDossierDisciplinaire key={ouvert.id} dossier={ouvert} occupe={chargement}
            onDossier={remplacer} onEtape={remplacerEtape} onRecharger={charger} />
        </DrawerCustom>
      )}
    </div>
  );
}

ListeDossiersDisciplinaires.propTypes = { employeId: PropTypes.number };

export default ListeDossiersDisciplinaires;
